import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import { faSearch, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import {
    BZ2_FACET_CATEGORY,
    BZ2_SEARCH_BUTTON,
    BZ2_SUGGEST_CATEGORY_CATEGORY,
    PRODUCT_CREATE_EDIT_FORM_I_WANT_TO_PICK_CATEGORY_MANUALLY,
    PRODUCT_CREATE_EDIT_FORM_LOADING_SUGGESTIONS,
} from 'mk/autogenerated/translations/CategorySelectModal.a0a86e6506f335a0e18c8e898b446ffe'
import { getLabel, isJobsCategory, isServicesCategory, list } from 'mk/bazaar/product/categories';
import CategoryListItem, { Category } from 'mk2/apps/bazaar/components/CategoryListItem';
import CategorySuggestionListItem, { CategorySuggestionListItemPlaceholder } from 'mk2/apps/bazaar/components/CategorySuggestionListItem';
import useFacetFiltering from 'mk2/apps/bazaar/hooks/useFacetFiltering';
import { Btn, BtnType } from 'mk2/components/Btn';
import { ClearableInput } from 'mk2/components/ClearableInput';
import { Input } from 'mk2/components/Input';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { Modal } from 'mk2/components/Modal';
import { Panel } from 'mk2/components/Panel';
import { animate } from 'mk2/helpers/animations';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import styles from './CategorySelectModal.mscss';

const ROW_HEIGHT = 44;
const emptyArray = [];
const listContainerProps = {
    'data-cy': 'category-select-virtualized-list',
};

interface OwnProps {
    top: number;
    left: number;
    width?: number;
    show: boolean;

    onClose();
}

type Props = OwnProps & CategorySelectOwnProps;

const CategorySelectModal: React.FunctionComponent<Props> = ({ top, left, width, show, onClose, ...rest }) => {
    return (
        <Modal show={show} className={styles.CategorySelectModal} backdrop style={{ left, top }} onHide={onClose}>
            <Panel className={styles.CategorySelectModal__container} style={{ width }}>
                <CategorySelect {...rest} className={styles.CategorySelectModal__select} />
            </Panel>
        </Modal>
    );
};

CategorySelectModal.displayName = 'CategorySelectModal';

export default React.memo<Props>(CategorySelectModal);

function categoryToOption(
    category: Category,
    {
        parents = emptyArray,
        level = 0,
        showIcon = false,
        servicesOverlimit = false,
        jobsOverlimit = false,
    }: {
        parents?: string[];
        level?: number;
        showIcon?: boolean;
        servicesOverlimit?: boolean;
        jobsOverlimit?: boolean;
    } = {},
): CategoryOption {
    const isOverlimitServices = servicesOverlimit && isServicesCategory(category.name);
    const isOverlimitJobs = jobsOverlimit && isJobsCategory(category.name);
    const isOverlimit = isOverlimitServices || isOverlimitJobs;

    const label = parents?.length
        ? `${parents.map(getLabel).join(' > ')} > ${getLabel(category.name)}`
        : getLabel(category.name);

    return {
        label,
        value: category.name,
        filterValue: label,
        hasChildren: Boolean(category.children),
        category,
        showIcon,
        level,
        parents,
        needsPlusPro: isOverlimitServices,
        needsPro: isOverlimitJobs,
        disabled: isOverlimit,
    };
}

function flattenCategoriesForFiltering(
    acc: CategoryOption[],
    cur: Category,
    servicesOverlimit: boolean,
    jobsOverlimit: boolean,
    parents: string[] = emptyArray,
): CategoryOption[] {
    return [
        ...acc,
        ...(cur.children
            ? cur.children.reduce<CategoryOption[]>(
                  (childAcc, childCur) =>
                      flattenCategoriesForFiltering(childAcc, childCur, servicesOverlimit, jobsOverlimit, [
                          ...parents,
                          cur.name,
                      ]),
                  [],
              )
            : [categoryToOption(cur, { parents, servicesOverlimit, jobsOverlimit })]),
    ];
}

function flattenCategories(
    acc: CategoryOption[],
    cur: Category,
    servicesOverlimit: boolean,
    jobsOverlimit: boolean,
    openedCategories: string[],
    level: number = 0,
): CategoryOption[] {
    return [
        ...acc,
        categoryToOption(cur, { level, showIcon: level === 0, servicesOverlimit, jobsOverlimit }),
        ...(openedCategories.includes(cur.name)
            ? cur.children.reduce<CategoryOption[]>(
                  (childAcc, childCur) =>
                      flattenCategories(
                          childAcc,
                          childCur,
                          servicesOverlimit,
                          jobsOverlimit,
                          openedCategories,
                          level + 1,
                      ),
                  [],
              )
            : []),
    ];
}

interface CategoryOption {
    label: React.ReactNode;
    value: string;
    filterValue: string;
    hasChildren: boolean;
    category: Category;
    parents: string[];
    showIcon: boolean;
    level: number;
    disabled: boolean;
    needsPlusPro: boolean;
    needsPro: boolean;
}

interface Suggestion {
    category: string;
    text: string;
}

interface CategorySelectOwnProps<TSuggestion extends Suggestion = Suggestion> {
    className?: string;
    selected: string;
    servicesOverlimit: boolean;
    jobsOverlimit: boolean;
    suggestions?: TSuggestion[];
    isLoadingSuggestions?: boolean;
    onCategoryClick(category: Category);
    onSuggestionClick?(suggestion: TSuggestion);
}

const CategorySelect: React.FunctionComponent<CategorySelectOwnProps> = ({
    className,
    selected,
    servicesOverlimit,
    jobsOverlimit,
    onCategoryClick,
    suggestions = emptyArray,
    isLoadingSuggestions = false,
    onSuggestionClick,
}) => {
    const categoriesForFiltering: CategoryOption[] = useMemo(
        () =>
            list().reduce<CategoryOption[]>(
                (acc, cur) => flattenCategoriesForFiltering(acc, cur, servicesOverlimit, jobsOverlimit),
                [],
            ),
        [servicesOverlimit, jobsOverlimit],
    );
    const [showAllCategories, setShowAllCategories] = useState<boolean>(false);
    const [openedCategories, setOpenedCategories] = useState<string[]>(() => {
        if (selected) {
            // NOTE: Category can be not found - e.g. when we restructure catalog ...
            // category split to subcategories
            return categoriesForFiltering.find((o) => o.value === selected)?.parents ?? [];
        } else {
            return [];
        }
    });
    const isSelected = useCallback((option: CategoryOption): boolean => selected === option.value, [selected]);
    const { options: filteredCategories, filterValue, onFilterChange, onFilterClear } = useFacetFiltering({
        options: categoriesForFiltering,
        isSelected,
    });
    const categories = useMemo(() => {
        return list().reduce<CategoryOption[]>(
            (acc, cur) => flattenCategories(acc, cur, servicesOverlimit, jobsOverlimit, openedCategories),
            [],
        );
    }, [openedCategories]);

    const options = filterValue ? filteredCategories : categories;
    const [hovering, setHovering] = useState<number>(null);

    const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(() =>
        selected ? options.findIndex((o) => o.value === selected) : undefined,
    );

    const onCategoryClickCallback = useEventCallback(
        (category: Category) => {
            if (category.children) {
                // Toggle category
                setOpenedCategories((s) =>
                    s.includes(category.name) ? s.filter((n) => n !== category.name) : [...s, category.name],
                );
                setScrollToIndex(undefined);
            } else {
                onCategoryClick(category);
            }
        },
        [onCategoryClick, setOpenedCategories, setScrollToIndex],
    );

    const onShowAllCategories = useEventCallback(() => {
        setShowAllCategories(true);
        setScrollToIndex(undefined);
    }, [setShowAllCategories, setScrollToIndex]);

    // Solving problem when last category has children and click on this category displays children
    // but user do not see that something has happen because children are overflowed
    const previousOpenedCategoriesRef = useRef<string[]>(openedCategories);
    const listRef = useRef<List>();
    useEffect(() => {
        // Opened categories has changed
        const justOpened = openedCategories.filter((c) => !previousOpenedCategoriesRef.current.includes(c));
        // Store previous opened categories value
        previousOpenedCategoriesRef.current = openedCategories;
        if (!justOpened.length) {
            // Nothing opened
            return;
        }

        const currentScrollTop = listRef.current.Grid.state.scrollTop;
        // Get index of currently opened category
        const scrollTo = options.findIndex((o) => o.value === justOpened[0]);
        // Get scroll position so that this opened category is last item in the viewport
        const end = listRef.current.getOffsetForRow({
            alignment: 'end',
            index: scrollTo,
        });

        // Scroll that we can see at least one and half of child items
        const y = end + 1.5 * ROW_HEIGHT;

        // Check if we really need to scroll
        if (currentScrollTop <= y && end > 0) {
            // Trigger scroll animation
            animate(
                currentScrollTop,
                y,
                100,
            )((value: number) => {
                listRef.current.scrollToPosition(value);
            });
        }
    }, [openedCategories, previousOpenedCategoriesRef, options]);

    const onCategoryMouseEnter = useEventCallback(
        (category: Category) => {
            setHovering(options.findIndex((opt) => opt.category === category));
        },
        [setHovering, options],
    );

    const onSearchKeyDown = useEventCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            const nonDisabledOptions = options.filter((opt) => !opt.disabled);
            if (!nonDisabledOptions.length) {
                return;
            }

            if (hovering === null) {
                switch (event.key) {
                    case 'ArrowUp':
                        setHovering(nonDisabledOptions.length - 1);
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        setHovering(0);
                        event.preventDefault();
                        break;
                }
            } else {
                let index = hovering;
                switch (event.key) {
                    case 'ArrowUp':
                        do {
                            index--;
                            if (index < 0) {
                                index = nonDisabledOptions.length - 1;
                            }
                        } while (nonDisabledOptions[index].disabled);
                        setHovering(index);
                        setScrollToIndex(index);
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        do {
                            index++;
                            if (index >= nonDisabledOptions.length) {
                                index = 0;
                            }
                        } while (nonDisabledOptions[index].disabled);
                        setHovering(index);
                        setScrollToIndex(index);
                        event.preventDefault();
                        break;
                    case 'Enter':
                        const option = nonDisabledOptions[hovering];
                        onCategoryClickCallback(option.category);
                        break;
                    default:
                        setHovering(null);
                }
            }
        },
        [setHovering, setScrollToIndex, hovering, options],
    );

    const rowRenderer = useCallback(
        ({ index, key, style }: ListRowProps) => {
            const option = options[index];
            return (
                <div
                    className={cx(
                        styles.CategorySelect__option,
                        styles[`CategorySelect__option--level${option.level}`],
                        hovering === index && styles['CategorySelect__option--hover'],
                    )}
                    key={key}
                    style={style}
                >
                    <CategoryListItem
                        label={option.label}
                        category={option.category}
                        disabled={option.disabled}
                        showIcon={option.showIcon}
                        needsPlusPro={option.needsPlusPro}
                        needsPro={option.needsPro}
                        selected={isSelected(option)}
                        hasMoreIcon={
                            <FontAwesomeIcon icon={openedCategories.includes(option.value) ? faAngleUp : faAngleDown} />
                        }
                        isLast // to disable bottom border
                        onClick={option.disabled ? null : onCategoryClickCallback}
                        onMouseEnter={option.disabled ? null : onCategoryMouseEnter}
                    />
                </div>
            );
        },
        [options, onCategoryClickCallback, isSelected, openedCategories, hovering],
    );

    // Show suggestions only if nothing selected and not loading suggestions
    const showSuggestions = !selected && !isLoadingSuggestions && suggestions?.length > 0 && !showAllCategories;

    return (
        <div className={cx(styles.CategorySelect, className)}>
            <div className={styles.CategorySelect__headline}>
                {isLoadingSuggestions
                    ? PRODUCT_CREATE_EDIT_FORM_LOADING_SUGGESTIONS
                    : showSuggestions
                    ? BZ2_SUGGEST_CATEGORY_CATEGORY
                    : BZ2_FACET_CATEGORY}
            </div>
            {isLoadingSuggestions ? (
                <div className={styles.CategorySelect__suggestions}>
                    <div className={styles.CategorySelect__suggestions__suggestions}>
                        <CategorySuggestionListItemPlaceholder />
                        <CategorySuggestionListItemPlaceholder isLast />
                    </div>
                    <div className={styles.CategorySelect__suggestions__showAll}>
                        <div className={styles.CategorySelect__suggestions__showAll__placeholder}>
                            <LoadingPlaceholder width={293} />
                        </div>
                    </div>
                </div>
            ) : showSuggestions ? (
                <div className={styles.CategorySelect__suggestions}>
                    <div className={styles.CategorySelect__suggestions__suggestions}>
                        {suggestions.map((suggestion, index) => (
                            <CategorySuggestionListItem
                                key={suggestion.category}
                                isLast={index + 1 === suggestions.length}
                                suggestion={suggestion}
                                onClick={onSuggestionClick}
                            />
                        ))}
                    </div>
                    <div className={styles.CategorySelect__suggestions__showAll}>
                        <Btn
                            type={BtnType.Link}
                            label={PRODUCT_CREATE_EDIT_FORM_I_WANT_TO_PICK_CATEGORY_MANUALLY}
                            onClick={onShowAllCategories}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <ClearableInput
                        className={styles.CategorySelect__filter}
                        icon={<FontAwesomeIcon icon={faSearch} />}
                        clearIcon={
                            <FontAwesomeIcon icon={faTimesCircle} className={styles.CategorySelect__clearIcon} />
                        }
                        hasValue={Boolean(filterValue)}
                        onClear={onFilterClear}
                    >
                        <Input
                            className={styles.CategorySelect__filter__input}
                            type="text"
                            onChange={onFilterChange}
                            placeholder={BZ2_SEARCH_BUTTON + '...'}
                            value={filterValue}
                            autoFocus
                            data-cy="category-select-filter"
                            onKeyDown={onSearchKeyDown}
                        />
                    </ClearableInput>

                    <div className={styles.FacetSelect__options}>
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <List
                                    ref={listRef}
                                    containerProps={listContainerProps}
                                    rowCount={options.length}
                                    rowHeight={ROW_HEIGHT}
                                    rowRenderer={rowRenderer}
                                    width={width}
                                    height={300}
                                    scrollToIndex={scrollToIndex}
                                />
                            )}
                        </AutoSizer>
                    </div>
                </>
            )}
        </div>
    );
};

CategorySelect.displayName = 'CategorySelect';
